import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  Switch,
  Chip,
  Divider,
  Grid
} from '@mui/material';
import { tenantApiFetch } from './tenantApi';
import { useTenantAuth } from '../context/TenantAuthContext';
import { TenantCan } from '../components/tenant/TenantCan';

function statusChipColor(s) {
  if (s === 'active') return 'success';
  if (s === 'archived') return 'default';
  return 'warning';
}

export default function TenantRolePermissionMatrixPage() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useTenantAuth();
  const canManage = hasPermission('tenant.roles.manage');

  const [matrix, setMatrix] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/roles/${roleId}/permission-matrix`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load matrix');
      setMatrix(json);
      const next = new Set();
      for (const r of json.rows || []) {
        if (r.assigned) next.add(r.code);
      }
      setSelected(next);
      setDirty(false);
    } catch (e) {
      setError(e.message);
      setMatrix(null);
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => {
    const m = new Map();
    for (const r of matrix?.rows || []) {
      const key = r.module_name || '—';
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [matrix]);

  const canEdit = Boolean(matrix?.canEdit && canManage);

  const toggle = (row, on) => {
    if (!canEdit || !row.canToggle) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(row.code);
      else next.delete(row.code);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/roles/${roleId}/permissions`, {
        method: 'PUT',
        body: { permissionCodes: Array.from(selected) }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Save failed');
      setMatrix(json);
      const next = new Set();
      for (const r of json.rows || []) {
        if (r.assigned) next.add(r.code);
      }
      setSelected(next);
      setDirty(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    if (!matrix) return;
    const next = new Set();
    for (const r of matrix.rows || []) {
      if (r.assigned) next.add(r.code);
    }
    setSelected(next);
    setDirty(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !matrix) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!matrix) return null;

  const { role, summary } = matrix;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4">Role permissions</Typography>
          <Typography variant="body2" color="text.secondary">
            {role.role_name} · <code>{role.role_code}</code>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate(`/tenant/roles/${roleId}`)}>
            Role detail
          </Button>
          <Button variant="outlined" onClick={() => navigate('/tenant/roles')}>
            All roles
          </Button>
        </Box>
      </Box>

      {role.is_system_role && (
        <Alert severity="info" sx={{ mb: 2 }}>
          System roles have a fixed permission set in this product. This page is read-only.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Effective permission summary
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Assigned (total)
            </Typography>
            <Typography variant="h6">{summary.totalAssigned}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Active
            </Typography>
            <Typography variant="h6">{summary.totalAssignedActive}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Inactive catalog (stale)
            </Typography>
            <Typography variant="h6">{summary.totalAssignedInactive}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Catalog size
            </Typography>
            <Typography variant="h6">{summary.totalCatalog}</Typography>
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          By module
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {Object.entries(summary.byModule || {}).map(([mod, v]) => (
            <Chip
              key={mod}
              size="small"
              label={`${mod}: ${v.assigned}/${v.catalog}`}
              variant="outlined"
            />
          ))}
        </Box>
      </Paper>

      <TenantCan permission="tenant.roles.manage">
        {canEdit && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button variant="contained" onClick={save} disabled={saving || !dirty}>
              {saving ? <CircularProgress size={22} /> : 'Save changes'}
            </Button>
            <Button onClick={discard} disabled={!dirty || saving}>
              Discard
            </Button>
          </Box>
        )}
      </TenantCan>

      {groups.map(([mod, rows]) => (
        <Paper key={mod} sx={{ mb: 2, overflow: 'auto' }}>
          <Box sx={{ px: 2, py: 1, bgcolor: 'grey.100', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {mod}
            </Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={72} align="center">
                  Assigned
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Catalog status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const on = selected.has(r.code);
                return (
                  <TableRow key={r.id} hover selected={on}>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={on}
                        onChange={(e) => toggle(r, e.target.checked)}
                        disabled={!canEdit || !r.canToggle}
                        inputProps={{ 'aria-label': r.code }}
                      />
                    </TableCell>
                    <TableCell>{r.permission_name}</TableCell>
                    <TableCell>
                      <code>{r.permission_code}</code>
                    </TableCell>
                    <TableCell>{r.action_name || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.status} color={statusChipColor(r.status)} />
                      {r.is_system_permission && (
                        <Chip size="small" label="System perm" sx={{ ml: 0.5 }} variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      ))}
    </Box>
  );
}
